import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type {
  AgentAuthorityResponse,
  CurrentUserSchema,
  UserRoleType,
} from '../heyapi/types.gen';

type AgentAuthorityFlags = Pick<
  AgentAuthorityResponse,
  'issuePolicy' | 'issueQuote' | 'canSubmitMidterms'
>;

type CurrentUserData = Pick<
  CurrentUserSchema,
  'id' | 'email' | 'firstName' | 'lastName' | 'role'
>;

interface PermissionsState {
  // User data
  currentUser: CurrentUserData | null;
  role: UserRoleType | null;

  // UI/Mode states
  editMode: boolean;
  isTimeTraveling: boolean;

  // Policy/User States
  isPolicyAssignedToMe: boolean | undefined;
  isPolicyReferredToMe: boolean | undefined;

  //! TODO: add policy transaction status and transaction type to the store
  /**
   * Logic Table for Policy Transaction Types & Status Mapping
   *
   * | Transaction Type      | Status         | Shown on table           |
   * |--------------------- |--------------- |------------------------ |
   * | POLICY_BOUND          | IN_PROGRESS    | Not Bound                |
   * | POLICY_BOUND          | ACTIVE         | Policy Bound             |
   * | POLICY_BOUND          | (Any other)    | Not Bound                |
   *
   * | POLICY_CANCELLED      | IN_PROGRESS    | Midterm (Cancellation)   |
   * | POLICY_CANCELLED      | ACTIVE         | Policy Cancelled         |
   * | POLICY_CANCELLED      | (Any other)    | Policy Bound             |
   * |                       |                |                          |
   * | POLICY_ENDORSEMENT    | IN_PROGRESS    | Midterm (Endorsement)    |
   * | POLICY_ENDORSEMENT    | ACTIVE         | Policy (Endorsed)        |
   * | POLICY_ENDORSEMENT    | (Any other)    | Policy Bound             |
   * |                       |                |                          |
   * | POLICY_REINSTATED     | IN_PROGRESS    | Midterm (Reinstate)      |
   * | POLICY_REINSTATED     | ACTIVE         | Policy Reinstated        |
   * | POLICY_REINSTATED     | (Any other)    | Policy Bound             |
   */

  // Agent Authority
  agentAuthorityFlags: AgentAuthorityFlags | undefined;

  // Actions
  setCurrentUser: (user: CurrentUserData | null) => void;
  setEditMode: (editMode: boolean) => void;
  setIsTimeTraveling: (isTimeTraveling: boolean) => void;
  setAgentAuthorityFlags: (
    authorityFlags: AgentAuthorityFlags | undefined
  ) => void;
  setIsPolicyAssignedToMe: (isPolicyAssignedToMe: boolean | undefined) => void;
  setIsPolicyReferredToMe: (isPolicyReferredToMe: boolean | undefined) => void;

  // Reset for logout
  reset: () => void;
}

const initialState = {
  currentUser: null,
  role: null,
  editMode: false,
  isTimeTraveling: false,
  agentAuthorityFlags: undefined,
  isPolicyAssignedToMe: undefined,
  isPolicyReferredToMe: undefined,
};

export const usePermissionsStore = create<PermissionsState>()(
  devtools(
    (set) => ({
      ...initialState,

      // Actions
      setCurrentUser: (user) =>
        set({
          currentUser: user,
          role: user?.role ?? null,
        }),

      setEditMode: (editMode) => set({ editMode }),

      setIsTimeTraveling: (isTimeTraveling) => set({ isTimeTraveling }),

      setAgentAuthorityFlags: (agentAuthorityFlags) =>
        set({ agentAuthorityFlags }),

      setIsPolicyAssignedToMe: (isPolicyAssignedToMe) =>
        set({ isPolicyAssignedToMe }),

      setIsPolicyReferredToMe: (isPolicyReferredToMe) =>
        set({ isPolicyReferredToMe }),

      // Reset
      reset: () => set(initialState),
    }),
    { name: 'PermissionsStore' }
  )
);
